import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Send, Sparkles, Loader2, MoreVertical, User, Video, Shield, Flag, Check, CheckCheck, MapPin, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import BlockReportModal from '../components/BlockReportModal';

const getImageUrl = (photoPath) => {
  if (!photoPath) return 'https://images.pexels.com/photos/1040881/pexels-photo-1040881.jpeg?auto=compress&cs=tinysrgb&w=100';
  if (photoPath.startsWith('http')) return photoPath;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  return `${backendUrl}${photoPath}`;
};

const Chat = () => {
  const { userId } = useParams();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [icebreakers, setIcebreakers] = useState([]);
  const [loadingIcebreakers, setLoadingIcebreakers] = useState(false);
  const [showBlockReport, setShowBlockReport] = useState(false);
  const [matchId, setMatchId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const [dateIdeas, setDateIdeas] = useState([]);
  const [loadingDateIdeas, setLoadingDateIdeas] = useState(false);
  const [showDateIdeas, setShowDateIdeas] = useState(false);

  const { user, token } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Send typing indicator
  const sendTypingIndicator = useCallback(async (typing) => {
    try {
      await axios.post(`${API}/chat/typing`, 
        { receiver_id: userId, is_typing: typing },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      // Silently fail
    }
  }, [userId, token]);

  // Handle input change with typing indicator
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    if (!isTyping) {
      setIsTyping(true);
      sendTypingIndicator(true);
    }
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Stop typing indicator after 2 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      sendTypingIndicator(false);
    }, 2000);
  };

  // Fetch typing status and online status
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const [typingRes, onlineRes] = await Promise.all([
          axios.get(`${API}/chat/typing/${userId}`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/status/online/${userId}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setOtherUserTyping(typingRes.data.is_typing);
        setIsOnline(onlineRes.data.is_online);
      } catch (err) {
        // Silently fail
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 3000);
    return () => clearInterval(interval);
  }, [userId, token]);

  // Update own online status
  useEffect(() => {
    const updateOnlineStatus = async () => {
      try {
        await axios.post(`${API}/status/online`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        // Silently fail
      }
    };

    updateOnlineStatus();
    const interval = setInterval(updateOnlineStatus, 60000);
    return () => clearInterval(interval);
  }, [token]);

  // Fetch date ideas
  const fetchDateIdeas = async () => {
    setLoadingDateIdeas(true);
    try {
      const res = await axios.post(`${API}/ai/date-ideas`, 
        { match_user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDateIdeas(res.data.date_ideas);
      setShowDateIdeas(true);
    } catch (err) {
      toast.error('Kunne ikke hente date-idéer');
    } finally {
      setLoadingDateIdeas(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const [messagesRes, profileRes] = await Promise.all([
        axios.get(`${API}/messages/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/profile/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setMessages(messagesRes.data);
      setOtherUser(profileRes.data);
      
      // Get match ID for video call
      const matchesRes = await axios.get(`${API}/matches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const match = matchesRes.data.find(m => 
        m.user1_id === userId || m.user2_id === userId ||
        (m.other_user && m.other_user.id === userId)
      );
      if (match) {
        setMatchId(match.id);
      }
    } catch (err) {
      toast.error('Kunne ikke indlæse chat');
    } finally {
      setLoading(false);
    }
  };

  const fetchIcebreakers = async () => {
    setLoadingIcebreakers(true);
    try {
      const res = await axios.post(`${API}/ai/icebreaker`, {
        target_user_id: userId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIcebreakers(res.data.icebreakers);
    } catch (err) {
      toast.error('Kunne ikke generere samtalestartere');
    } finally {
      setLoadingIcebreakers(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    // Poll for new messages every 5 seconds
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [userId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (content) => {
    if (!content.trim()) return;
    
    setSending(true);
    try {
      const res = await axios.post(`${API}/messages`, {
        receiver_id: userId,
        content: content.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages([...messages, res.data]);
      setNewMessage('');
      setIcebreakers([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke sende besked');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(newMessage);
  };

  const selectIcebreaker = (text) => {
    // Put text in input field for editing instead of sending directly
    setNewMessage(text);
    setIcebreakers([]); // Hide icebreakers panel
    inputRef.current?.focus(); // Focus the input field
  };

  const handleBlocked = () => {
    navigate('/messages');
  };

  const startVideoCall = () => {
    if (matchId) {
      navigate(`/video-call/${matchId}`);
    } else {
      toast.error('Kunne ikke starte video opkald');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          
          <Link to={`/profile/${userId}`} className="flex items-center gap-3 flex-1">
            <div className="relative">
              <img
                src={getImageUrl(otherUser?.photos?.[0])}
                alt={otherUser?.name}
                className="w-10 h-10 rounded-full object-cover"
              />
              {/* Online indicator */}
              {isOnline && (
                <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-background rounded-full" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-foreground">{otherUser?.name}</h3>
                {otherUser?.is_verified && (
                  <span className="text-primary" title="Verificeret profil">✓</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {otherUserTyping ? (
                  <span className="text-primary flex items-center gap-1">
                    <span className="flex gap-0.5">
                      <span className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </span>
                    skriver...
                  </span>
                ) : isOnline ? (
                  <span className="text-green-500">Online nu</span>
                ) : (
                  otherUser?.location || 'Lokation ukendt'
                )}
              </p>
            </div>
          </Link>

          {/* Video Call Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={startVideoCall}
            className="rounded-full"
            data-testid="video-call-btn"
            title="Video opkald"
          >
            <Video className="w-5 h-5 text-primary" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full" data-testid="chat-menu-btn">
                <MoreVertical className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/profile/${userId}`)}>
                <User className="w-4 h-4 mr-2" />
                Se Profil
              </DropdownMenuItem>
              <DropdownMenuItem onClick={startVideoCall}>
                <Video className="w-4 h-4 mr-2" />
                Video Opkald
              </DropdownMenuItem>
              <DropdownMenuItem onClick={fetchIcebreakers}>
                <Sparkles className="w-4 h-4 mr-2" />
                Få Samtalestartere
              </DropdownMenuItem>
              <DropdownMenuItem onClick={fetchDateIdeas} disabled={loadingDateIdeas}>
                <Calendar className="w-4 h-4 mr-2" />
                {loadingDateIdeas ? 'Henter...' : 'Få Date-idéer'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowBlockReport(true)} className="text-destructive">
                <Shield className="w-4 h-4 mr-2" />
                Bloker / Rapporter
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 pt-20 pb-32 px-4 overflow-y-auto">
        <div className="max-w-2xl mx-auto space-y-4 py-4">
          {messages.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <p className="text-foreground font-medium mb-2">
                Start samtalen med {otherUser?.name}!
              </p>
              <p className="text-muted-foreground text-sm mb-4">
                Brug AI til at generere en perfekt samtalestarter
              </p>
              {icebreakers.length === 0 && (
                <Button
                  onClick={fetchIcebreakers}
                  disabled={loadingIcebreakers}
                  className="rounded-full bg-gradient-to-r from-primary to-accent hover:opacity-90"
                  data-testid="get-icebreakers-btn"
                >
                  {loadingIcebreakers ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Få AI Samtalestartere
                </Button>
              )}
            </div>
          ) : (
            <>
              {messages.map((msg, index) => {
                const isMe = msg.sender_id === user.id;
                const isLastMessage = index === messages.length - 1;
                return (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02 }}
                    className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${
                        isMe
                          ? 'bg-primary text-primary-foreground rounded-br-md'
                          : 'bg-muted text-foreground rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                      <div className={`flex items-center gap-1 mt-1 ${isMe ? 'justify-end' : ''}`}>
                        <span className={`text-[10px] ${isMe ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {/* Read receipt for sent messages */}
                        {isMe && (
                          <span className={`${msg.read ? 'text-blue-300' : 'text-primary-foreground/50'}`}>
                            {msg.read ? (
                              <CheckCheck className="w-3.5 h-3.5" />
                            ) : (
                              <Check className="w-3.5 h-3.5" />
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
              
              {/* Typing indicator */}
              {otherUserTyping && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start"
                >
                  <div className="bg-muted px-4 py-3 rounded-2xl rounded-bl-md">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </motion.div>
              )}
              
              {/* Floating icebreaker button when there are messages */}
              {icebreakers.length === 0 && (
                <div className="flex justify-center pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchIcebreakers}
                    disabled={loadingIcebreakers}
                    className="rounded-full border-primary/30 hover:border-primary/50 hover:bg-primary/5"
                    data-testid="get-more-icebreakers-btn"
                  >
                    {loadingIcebreakers ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2 text-primary" />
                    )}
                    <span className="text-sm">Få samtalestartere</span>
                  </Button>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Ice Breakers - More prominent display */}
      {icebreakers.length > 0 && (
        <div className="fixed bottom-20 left-0 right-0 z-30 px-4 pb-4">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-primary/10 via-accent/10 to-primary/5 backdrop-blur-lg border border-primary/20 rounded-2xl p-4 shadow-lg"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <span className="font-semibold text-foreground">AI Samtalestartere</span>
                  <p className="text-xs text-muted-foreground">Tryk for at redigere før du sender</p>
                </div>
              </div>
              <div className="space-y-2">
                {icebreakers.map((icebreaker, index) => (
                  <motion.button
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => selectIcebreaker(icebreaker)}
                    className="w-full text-left p-3 bg-background/80 hover:bg-background border border-border/50 rounded-xl text-sm text-foreground hover:border-primary/50 transition-all shadow-sm group"
                    data-testid={`icebreaker-${index}`}
                  >
                    <span className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">💬</span>
                      <span className="flex-1">{icebreaker}</span>
                      <span className="text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">✏️ Rediger</span>
                    </span>
                  </motion.button>
                ))}
              </div>
              <button
                onClick={() => setIcebreakers([])}
                className="mt-3 text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-center"
              >
                Skjul samtalestartere
              </button>
            </motion.div>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-border p-4">
        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto flex items-center gap-3">
          <Input
            ref={inputRef}
            value={newMessage}
            onChange={handleInputChange}
            placeholder="Skriv en besked..."
            className="flex-1 h-12 rounded-full px-5"
            disabled={sending}
            data-testid="message-input"
          />
          <Button
            type="submit"
            size="icon"
            className="w-12 h-12 rounded-full bg-primary hover:bg-primary/90"
            disabled={!newMessage.trim() || sending}
            data-testid="send-message-btn"
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </form>
      </div>

      {/* Date Ideas Modal */}
      <AnimatePresence>
        {showDateIdeas && dateIdeas.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setShowDateIdeas(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card rounded-3xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-display font-bold text-foreground">Date-idéer</h2>
                <p className="text-muted-foreground mt-1">
                  Forslag til jeres første date med {otherUser?.name}
                </p>
              </div>

              <div className="space-y-3">
                {dateIdeas.map((idea, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="p-4 bg-muted/50 rounded-xl hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => {
                      setNewMessage(`Hvad siger du til: ${idea.title}? ${idea.description}`);
                      setShowDateIdeas(false);
                      inputRef.current?.focus();
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">
                        {idea.category === 'outdoor' ? '🌳' : 
                         idea.category === 'dining' ? '🍽️' : 
                         idea.category === 'entertainment' ? '🎬' :
                         idea.category === 'adventure' ? '🎢' : '☕'}
                      </span>
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground">{idea.title}</h4>
                        <p className="text-sm text-muted-foreground mt-1">{idea.description}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <p className="text-xs text-center text-muted-foreground mt-4">
                Tryk på en idé for at foreslå den
              </p>

              <Button
                variant="ghost"
                onClick={() => setShowDateIdeas(false)}
                className="w-full mt-4 rounded-full"
              >
                Luk
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Block/Report Modal */}
      <BlockReportModal
        isOpen={showBlockReport}
        onClose={() => setShowBlockReport(false)}
        userId={userId}
        userName={otherUser?.name}
        onBlocked={handleBlocked}
      />
    </div>
  );
};

export default Chat;
